import { useState, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { fetchAllFiles, fetchFileById } from '../features/organization/organizationSlice';

/**
 * Custom hook to manage file navigation state across components
 * Provides shared state management for currentSelected and expandedKeys
 *
 * @returns {Object} Navigation state and methods
 */
const useFileNavigation = () => {
  const dispatch = useDispatch();
  const params = useParams();
  const { activeWorkspace, folderMappings } = useSelector((state) => state.organization);

  // Initialize state with values from localStorage if available
  const [currentSelected, setCurrentSelected] = useState(() => {
    const saved = localStorage.getItem('oneDoc_currentSelected');
    return saved ? saved : '';
  });

  const [expandedKeys, setExpandedKeys] = useState(() => {
    const saved = localStorage.getItem('oneDoc_expandedKeys');
    return saved ? JSON.parse(saved) : [];
  });

  // Save state to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('oneDoc_currentSelected', currentSelected);
  }, [currentSelected]);

  useEffect(() => {
    localStorage.setItem('oneDoc_expandedKeys', JSON.stringify(expandedKeys));
  }, [expandedKeys]);

  /**
   * Set the currently selected file/folder and persist to localStorage
   * @param {string} id - The ID of the selected file or folder
   */
  const setSelectedItem = useCallback((id) => {
    setCurrentSelected(id);
  }, []);

  /**
   * Toggle a folder's expanded state
   * @param {string} folderId - The ID of the folder to toggle
   */
  const toggleFolder = useCallback((folderId) => {
    setExpandedKeys((prevKeys) => {
      if (prevKeys.includes(folderId)) {
        return prevKeys.filter((key) => key !== folderId);
      } else {
        return [...prevKeys, folderId];
      }
    });
  }, []);

  /**
   * Expand a folder and all its parents
   * @param {string} folderId - The ID of the folder to expand
   */
  const expandFolder = useCallback(
    (folderId) => {
      if (!folderId || !folderMappings) return;

      const allKeysToExpand = [];
      let _parent = folderId;

      // Add the folder and all its parents to the keys to expand
      while (_parent !== null && _parent !== undefined) {
        const mpItem = folderMappings[_parent];
        allKeysToExpand.push(_parent);
        _parent = mpItem?.parent_id_id;
      }

      setExpandedKeys((prevKeys) => {
        const keys = [...prevKeys];
        allKeysToExpand.forEach((key) => {
          if (!keys.includes(key)) keys.push(key);
        });
        return keys;
      });
    },
    [folderMappings]
  );

  /**
   * Collapse a folder
   * @param {string} folderId - The ID of the folder to collapse
   */
  const collapseFolder = useCallback((folderId) => {
    setExpandedKeys((prevKeys) => prevKeys.filter((key) => key !== folderId));
  }, []);

  /**
   * Clear all expanded folders
   */
  const clearExpandedFolders = useCallback(() => {
    setExpandedKeys([]);
  }, []);

  /**
   * Select a file and load its contents
   * @param {string} fileId - The ID of the file to select
   * @param {string} folderId - The ID of the parent folder
   * @param {boolean} isApiFile - Whether this is an API file
   */
  const selectFile = useCallback(
    (fileId, folderId, isApiFile = false) => {
      if (!fileId) return;

      // Use setSelectedItem instead of directly calling setCurrentSelected
      setSelectedItem(fileId);

      if (folderId) {
        // Expand the parent folder and its ancestors
        expandFolder(folderId);

        // Fetch all files in this folder if needed
        dispatch(
          fetchAllFiles({
            folder_id: folderId,
            workspace_id: activeWorkspace?.id,
          })
        );
      }

      // For API files, also expand the API ID
      if (isApiFile) {
        setExpandedKeys((prevKeys) => {
          const apiKey = fileId + '-openApiId';
          if (prevKeys.includes(apiKey)) return prevKeys;
          return [...prevKeys, apiKey];
        });
      }

      // Fetch the file data
      dispatch(
        fetchFileById({
          id: fileId,
          isApiFile,
          workspace_id: activeWorkspace?.id,
        })
      );
    },
    [activeWorkspace?.id, dispatch, expandFolder, setSelectedItem]
  );

  /**
   * Initialize navigation state based on URL params
   */
  const initializeFromParams = useCallback(() => {
    const fileId = params?.pageId;
    const folderId = params?.folderId;

    if (fileId) {
      selectFile(fileId, folderId);
    } else if (folderId) {
      expandFolder(folderId);
      setSelectedItem(folderId);
    }
  }, [params?.pageId, params?.folderId, selectFile, expandFolder, setSelectedItem]);

  return {
    currentSelected,
    setCurrentSelected: setSelectedItem,
    expandedKeys,
    setExpandedKeys,
    toggleFolder,
    expandFolder,
    collapseFolder,
    clearExpandedFolders,
    selectFile,
    initializeFromParams,
  };
};

export default useFileNavigation;
